import LockIcon from '@mui/icons-material/Lock'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import FormControlLabel from '@mui/material/FormControlLabel'
import Typography from '@mui/material/Typography'
import React from 'react'

import type { SchemaCampaignQuota, SchemaQuotaEdit } from '@/__generated/es-api'
import Dialog from '@/app/component/atom/dialog'
import DialogHeader from '@/app/component/atom/dialog/header'
import { useConfirmation } from '@/app/contexts/confirmation.context'
import type { Condition, PersonalizationType } from '@/app/types'

import {
  areEditableQuotaConditionsValid,
  fromEditableQuotaConditions,
  toEditableQuotaConditions,
} from './quota-conditions'
import { SharedQuotaForm, SingleQuotaForm } from './quota-form'
import type { QuotaMetadataFormValues } from './quota-form-validation'

type Props = {
  campaignCount: number
  isUpdating: boolean
  personalizationList: PersonalizationType
  quota: SchemaCampaignQuota
  onClose: () => void
  onSaveConditions: (quotaId: string, conditions: Condition | null) => void
  onSaveMetadata: (quotaId: string, quota: Required<SchemaQuotaEdit>) => Promise<boolean>
  onSaveQuota: (quotaId: string, quota: Required<SchemaQuotaEdit>, conditions: Condition | null) => Promise<boolean>
}

type UnlockConfirmationContentProps = {
  campaignCount: number
  onAcknowledgementChange: (acknowledged: boolean) => void
}

const EDIT_QUOTA_FORM_ID = 'edit-quota-form'

const UnlockConfirmationContent: React.FC<UnlockConfirmationContentProps> = ({
  campaignCount,
  onAcknowledgementChange,
}) => {
  const [acknowledged, setAcknowledged] = React.useState(false)

  return (
    <>
      <Typography variant="body2">
        This quota is used in <strong>{campaignCount} campaigns</strong>. Changing its name or goal changes it{' '}
        <strong>everywhere it is used</strong>—not just this campaign.
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={acknowledged}
            onChange={(event) => {
              setAcknowledged(event.target.checked)
              onAcknowledgementChange(event.target.checked)
            }}
          />
        }
        label="I understand this affects all campaigns using this quota."
        sx={{ mt: 2 }}
      />
    </>
  )
}

const QuotaEditDialog: React.FC<Props> = ({
  campaignCount,
  isUpdating,
  personalizationList,
  quota,
  onClose,
  onSaveConditions,
  onSaveMetadata,
  onSaveQuota,
}) => {
  const createConfirmation = useConfirmation()
  const editableConditions = React.useMemo(() => toEditableQuotaConditions(quota.conditions), [quota.conditions])
  const [conditionsDirty, setConditionsDirty] = React.useState(false)
  const [conditionsValidationRequested, setConditionsValidationRequested] = React.useState(false)
  const [conditionGroups, setConditionGroups] = React.useState(editableConditions.groups)
  const [metadataDirty, setMetadataDirty] = React.useState(false)
  const [metadataUnlocked, setMetadataUnlocked] = React.useState(false)
  const [savedMetadata, setSavedMetadata] = React.useState(() => ({
    goal: String(quota.quotaGoal),
    name: quota.quotaName,
  }))

  const isShared = campaignCount > 1
  const metadataLocked = isShared && !metadataUnlocked
  const conditionsValid = !editableConditions.supported || areEditableQuotaConditionsValid(conditionGroups)
  const quotaFormDirty = conditionsDirty || (!isShared && !metadataLocked && metadataDirty)
  const usesSharedQuotaForm = isShared

  const handleSaveMetadata = async ({ goal, name }: QuotaMetadataFormValues) => {
    const numericGoal = Number(goal)
    const success = await onSaveMetadata(quota.quotaId, { goal: numericGoal, name })
    if (success) {
      setMetadataUnlocked(false)
      setSavedMetadata({ goal: String(numericGoal), name })
    }
  }

  const handleSaveSingleQuota = async ({ goal, name }: QuotaMetadataFormValues) => {
    if (!quotaFormDirty) {
      return
    }

    setConditionsValidationRequested(true)
    if (!conditionsValid) {
      return
    }

    const conditions = editableConditions.supported
      ? fromEditableQuotaConditions(conditionGroups)
      : (quota.conditions as Condition | null)
    const numericGoal = Number(goal)
    const success = await onSaveQuota(quota.quotaId, { goal: numericGoal, name }, conditions)
    if (!success) {
      return
    }

    setConditionsDirty(false)
    setConditionsValidationRequested(false)
    setMetadataDirty(false)
    setSavedMetadata({ goal: String(numericGoal), name })
    onClose()
  }

  const handleCancelMetadata = () => {
    setMetadataUnlocked(false)
    setSavedMetadata((currentMetadata) => ({ ...currentMetadata }))
  }

  const handleConditionsChange = (groups: Condition[]) => {
    setConditionsDirty(true)
    setConditionGroups(groups)
  }

  const handleUnlockMetadata = async () => {
    const confirmed = await createConfirmation({
      confirmButtonDisabled: true,
      confirmButtonLabel: 'Unlock & edit',
      content: ({ setConfirmButtonDisabled }) => (
        <UnlockConfirmationContent
          campaignCount={campaignCount}
          onAcknowledgementChange={(acknowledged) => setConfirmButtonDisabled(!acknowledged)}
        />
      ),
      title: 'Edit shared name & goal?',
    })

    if (confirmed) {
      setMetadataUnlocked(true)
    }
  }

  const handleSaveConditions = () => {
    if (!conditionsDirty) {
      return
    }

    setConditionsValidationRequested(true)
    if (!conditionsValid) {
      return
    }

    const conditions = editableConditions.supported
      ? fromEditableQuotaConditions(conditionGroups)
      : (quota.conditions as Condition | null)

    onSaveConditions(quota.quotaId, conditions)
    setConditionsDirty(false)
    setConditionsValidationRequested(false)
    onClose()
  }

  const metadataHeader = (
    <>
      <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography fontWeight={600} variant="subtitle2">
          Name &amp; goal
        </Typography>
        {isShared && !metadataUnlocked && (
          <Button size="small" startIcon={<LockIcon />} onClick={handleUnlockMetadata}>
            Unlock to edit
          </Button>
        )}
        {isShared && metadataUnlocked && (
          <Typography color="warning.main" sx={{ alignItems: 'center', display: 'flex', gap: 0.75 }} variant="caption">
            <LockOpenIcon fontSize="small" /> Editing affects all campaigns
          </Typography>
        )}
      </Box>
      {isShared && !metadataUnlocked && (
        <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
          This quota is shared across campaigns. Unlock name and goal to change them{' '}
          <strong>everywhere it is used</strong>. Conditions below apply only to this campaign.
        </Typography>
      )}
    </>
  )

  return (
    <Dialog fullWidth maxWidth="md" open onClose={isUpdating ? undefined : onClose}>
      <DialogHeader
        closeButtonLabel="Close edit quota dialog"
        disabled={isUpdating}
        title="Edit quota"
        onClose={onClose}
      />
      <DialogContent>
        {usesSharedQuotaForm ? (
          <SharedQuotaForm
            conditionGroups={conditionGroups}
            conditionsError={conditionsValidationRequested}
            conditionsSupported={editableConditions.supported}
            defaultValues={savedMetadata}
            isUpdating={isUpdating}
            metadataDisabled={metadataLocked || isUpdating}
            metadataHeader={metadataHeader}
            metadataUnlocked={metadataUnlocked}
            personalizationList={personalizationList}
            onCancelMetadata={handleCancelMetadata}
            onConditionsChange={handleConditionsChange}
            onSubmit={handleSaveMetadata}
          />
        ) : (
          <SingleQuotaForm
            conditionGroups={conditionGroups}
            conditionsError={conditionsValidationRequested}
            conditionsSupported={editableConditions.supported}
            defaultValues={savedMetadata}
            id={EDIT_QUOTA_FORM_ID}
            metadataDisabled={isUpdating}
            personalizationList={personalizationList}
            onConditionsChange={handleConditionsChange}
            onDirtyChange={setMetadataDirty}
            onSubmit={handleSaveSingleQuota}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: conditionGroups.length ? 'space-between' : 'flex-end' }}>
        {conditionGroups.length > 0 && (
          <Button color="error" disabled={metadataUnlocked} onClick={() => handleConditionsChange([])}>
            Remove all conditions
          </Button>
        )}
        <Button
          disabled={isUpdating || metadataUnlocked || !quotaFormDirty}
          form={usesSharedQuotaForm ? undefined : EDIT_QUOTA_FORM_ID}
          loading={isUpdating && !isShared}
          type={usesSharedQuotaForm ? 'button' : 'submit'}
          variant="contained"
          onClick={usesSharedQuotaForm ? handleSaveConditions : () => setConditionsValidationRequested(true)}
        >
          Save quota
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default QuotaEditDialog
